'use client';

import { useState } from 'react';
import { signIn } from 'next-auth/react';
import { FaYandex } from 'react-icons/fa';

import { PublicLink } from '@/components/PublicLink';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SurfacePanel } from '@/components/site/page-primitives';
import {
  buildPostAuthRedirect,
  type CheckoutPlanSlug,
  withCheckoutPlanLink,
} from '@/lib/auth-intent';
import { waitForAuthenticatedSession } from '@/lib/auth-session-sync';
import { getAuthProviderViaLabel } from '@/lib/authCopy';
import {
  resolvePublicYandexAuthState,
  signInWithPublicYandex,
} from '@/lib/public-yandex-auth';
import {
  getLoginIntroDescription,
  shouldShowYandexAuthCallToAction,
} from '@/lib/authUi';
import { useToast } from '@/hooks/use-toast';

type LoginFormErrors = Partial<Record<'email' | 'password', string>>;

function validateLoginForm(email: string, password: string): LoginFormErrors {
  const errors: LoginFormErrors = {};

  if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
    errors.email = 'Введите корректный адрес электронной почты';
  }

  if (password.length < 6) {
    errors.password = 'Пароль должен содержать минимум 6 символов';
  }

  return errors;
}

function FieldError({ message }: { message?: string }) {
  if (!message) {
    return null;
  }

  return <p className="text-sm text-destructive">{message}</p>;
}

export function LoginFormClient({
  checkoutPlan,
  yandexAuthEnabled,
  yandexClientId,
}: {
  checkoutPlan: CheckoutPlanSlug | null;
  yandexAuthEnabled: boolean;
  yandexClientId: string | null;
}) {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<LoginFormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);

  const postAuthRedirect = buildPostAuthRedirect(checkoutPlan);
  const { yandexAuthEntrypointEnabled } = resolvePublicYandexAuthState({
    yandexAuthEnabled,
    yandexClientId,
  });
  const showYandexAuth = shouldShowYandexAuthCallToAction(yandexAuthEntrypointEnabled);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors = validateLoginForm(email, password);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await signIn('credentials', {
        redirect: false,
        email: email.trim(),
        password,
        callbackUrl: postAuthRedirect,
      });

      if (result?.error) {
        throw new Error(result.error);
      }
      if (result?.ok === false) {
        throw new Error('Не удалось завершить вход');
      }

      await waitForAuthenticatedSession();

      toast({
        title: 'Вход выполнен',
        description: 'С возвращением в S-SHOT.',
      });
      window.location.assign(result?.url ?? postAuthRedirect);
    } catch {
      toast({
        variant: 'destructive',
        title: 'Не удалось войти',
        description: 'Проверьте адрес электронной почты и пароль',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'yandex') => {
    const providerLabel = getAuthProviderViaLabel(provider);

    try {
      setSocialLoading(provider);
      await signInWithPublicYandex({
        callbackUrl: postAuthRedirect,
        yandexAuthEnabled,
        yandexClientId,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Не удалось войти',
        description: error instanceof Error ? error.message : `Не удалось войти через ${providerLabel}`,
      });
    } finally {
      setSocialLoading(null);
    }
  };

  return (
    <SurfacePanel className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-semibold tracking-[-0.03em] text-[var(--site-ink-strong)]">Вход</h2>
        <p className="mt-2 text-sm text-[var(--site-ink-muted)]">
          {getLoginIntroDescription(checkoutPlan, yandexAuthEntrypointEnabled)}
        </p>
      </div>

      {showYandexAuth ? (
        <>
          <div className="flex flex-col space-y-4">
            <Button
              type="button"
              variant="outline"
              className="flex w-full items-center justify-center gap-2 py-5"
              onClick={() => handleSocialLogin('yandex')}
              disabled={Boolean(socialLoading)}
              data-testid="login-yandex-button"
            >
              {socialLoading === 'yandex' ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-[var(--site-primary-fill)] border-t-transparent" />
              ) : (
                <FaYandex className="text-[#fc3f1d]" />
              )}
              <span>Продолжить через Yandex</span>
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[color:var(--site-border-soft)]" />
            </div>
            <div className="relative flex justify-center text-sm font-medium">
              <span className="bg-[var(--site-surface-strong)] px-4 text-[var(--site-ink-subtle)]">Или</span>
            </div>
          </div>
        </>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div className="space-y-2">
          <label htmlFor="login-email" className="text-sm font-medium leading-none">
            Электронная почта
          </label>
          <Input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="ivan@example.com"
            data-testid="login-email-input"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (errors.email) {
                setErrors((current) => ({ ...current, email: undefined }));
              }
            }}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'login-email-error' : undefined}
          />
          <div id="login-email-error">
            <FieldError message={errors.email} />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="login-password" className="text-sm font-medium leading-none">
            Пароль
          </label>
          <Input
            id="login-password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            data-testid="login-password-input"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              if (errors.password) {
                setErrors((current) => ({ ...current, password: undefined }));
              }
            }}
            aria-invalid={Boolean(errors.password)}
            aria-describedby={errors.password ? 'login-password-error' : undefined}
          />
          <div id="login-password-error">
            <FieldError message={errors.password} />
          </div>
          <div className="text-right">
            <PublicLink href="/forgot-password" className="text-sm font-medium text-[var(--site-accent-text)] hover:underline">
              Забыли пароль?
            </PublicLink>
          </div>
        </div>

        <div className="pt-2">
          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
            data-testid="login-submit-button"
          >
            {isLoading ? 'Входим...' : 'Войти по электронной почте'}
          </Button>
        </div>
      </form>

      <div className="text-center text-sm">
        <p className="text-[var(--site-ink-muted)]">
          Нет аккаунта?{' '}
          <PublicLink
            href={withCheckoutPlanLink('/signup', checkoutPlan)}
            className="font-medium text-[var(--site-accent-text)] hover:underline"
          >
            Зарегистрироваться
          </PublicLink>
        </p>
        <p className="mt-3 text-[var(--site-ink-muted)]">
          Нужна помощь с доступом к аккаунту?{' '}
          <PublicLink href="/help" className="font-medium text-[var(--site-accent-text)] hover:underline">
            Откройте справку
          </PublicLink>
        </p>
      </div>
    </SurfacePanel>
  );
}
